import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Lib } from '../../libs/Lib';
import { ISceneBlock, ISceneItemsUpdate, Id } from '../interfaces';

export interface ColorPalette {
  name: string;
  category: string;
  primaryAccent: string;
  secondaryAccents: string[];
  headingColor: string;
  componentColor: string;
  cardBackground: string;
  pageBackground: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaletteService {
  private currentPalette$ = new BehaviorSubject<ColorPalette | null>(null);
  private palettes: ColorPalette[] = [
    // === DARK / NIGHT ===
    {
      name: 'Dark Night',
      category: 'Dark / Night',
      primaryAccent: '#2C3E50',
      secondaryAccents: ['#34495E', '#1C2833', '#283747', '#212F3C', '#1B2631'],
      headingColor: '#ECF0F1',
      componentColor: '#BDC3C7',
      cardBackground: '#1A1A1A',
      pageBackground: '#0D0D0D'
    },
    {
      name: 'Midnight Blue',
      category: 'Dark / Night',
      primaryAccent: '#0D47A1',
      secondaryAccents: ['#1565C0', '#1976D2', '#1E88E5', '#2196F3', '#42A5F5'],
      headingColor: '#01579B',
      componentColor: '#0277BD',
      cardBackground: '#E3F2FD',
      pageBackground: '#F0F7FF'
    },
    {
      name: 'Onyx Black',
      category: 'Dark / Night',
      primaryAccent: '#212121',
      secondaryAccents: ['#424242', '#616161', '#757575', '#9E9E9E', '#BDBDBD'],
      headingColor: '#000000',
      componentColor: '#212121',
      cardBackground: '#FAFAFA',
      pageBackground: '#FFFFFF'
    },
    {
      name: 'Charcoal Grey',
      category: 'Dark / Night',
      primaryAccent: '#424242',
      secondaryAccents: ['#616161', '#757575', '#9E9E9E', '#BDBDBD', '#E0E0E0'],
      headingColor: '#212121',
      componentColor: '#424242',
      cardBackground: '#FAFAFA',
      pageBackground: '#FFFFFF'
    },
    {
      name: 'Twilight',
      category: 'Dark / Night',
      primaryAccent: '#283593',
      secondaryAccents: ['#3949AB', '#3F51B5', '#5C6BC0', '#7986CB', '#9FA8DA'],
      headingColor: '#1A237E',
      componentColor: '#283593',
      cardBackground: '#E8EAF6',
      pageBackground: '#F3F4FB'
    },

    // === VIBRANT / RAINBOW ===
    {
      name: 'Rainbow Dreams',
      category: 'Vibrant / Rainbow',
      primaryAccent: '#FF6B6B',
      secondaryAccents: ['#FFD93D', '#6BCF7F', '#4ECDC4', '#A78BFA', '#F472B6', '#FB923C', '#34D399'],
      headingColor: '#1F2937',
      componentColor: '#4B5563',
      cardBackground: '#FFFFFF',
      pageBackground: '#F9FAFB'
    },
    {
      name: 'Citrus Burst',
      category: 'Vibrant / Rainbow',
      primaryAccent: '#FF9800',
      secondaryAccents: ['#FFB74D', '#FFCC80', '#FFE0B2', '#FFF3E0', '#FB8C00'],
      headingColor: '#E65100',
      componentColor: '#EF6C00',
      cardBackground: '#FFF3E0',
      pageBackground: '#FFFAF5'
    },
    {
      name: 'Sunset Vibes',
      category: 'Vibrant / Rainbow',
      primaryAccent: '#FF6F00',
      secondaryAccents: ['#FF8F00', '#FFA726', '#FFB74D', '#FF7043', '#E64A19'],
      headingColor: '#E65100',
      componentColor: '#BF360C',
      cardBackground: '#FFF3E0',
      pageBackground: '#FFFAF5'
    },
    {
      name: 'Tangerine Dream',
      category: 'Vibrant / Rainbow',
      primaryAccent: '#FF6F00',
      secondaryAccents: ['#FF8F00', '#FFA726', '#FFB74D', '#FFCC80', '#FFE0B2'],
      headingColor: '#E65100',
      componentColor: '#EF6C00',
      cardBackground: '#FFF3E0',
      pageBackground: '#FFFAF5'
    },
    {
      name: 'Hot Pink',
      category: 'Vibrant / Rainbow',
      primaryAccent: '#EC407A',
      secondaryAccents: ['#F06292', '#F48FB1', '#F8BBD0', '#FCE4EC', '#E91E63'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },

    // === EARTH / DESERT ===
    {
      name: 'Earth Tones',
      category: 'Earth / Desert',
      primaryAccent: '#8B4513',
      secondaryAccents: ['#A0522D', '#6B4423', '#9C6644', '#8B7355', '#7B5D3F'],
      headingColor: '#3E2723',
      componentColor: '#5D4037',
      cardBackground: '#F5F5DC',
      pageBackground: '#FAF8F3'
    },
    {
      name: 'Desert Sand',
      category: 'Earth / Desert',
      primaryAccent: '#D4A574',
      secondaryAccents: ['#DEB887', '#F5DEB3', '#EDC9A3', '#E8C4A3', '#C9A67A'],
      headingColor: '#8B7355',
      componentColor: '#A0826D',
      cardBackground: '#FAF4ED',
      pageBackground: '#FFFBF5'
    },
    {
      name: 'Warm Terracotta',
      category: 'Earth / Desert',
      primaryAccent: '#A0522D',
      secondaryAccents: ['#BC8F8F', '#CD853F', '#DEB887', '#D2B48C', '#F4A460'],
      headingColor: '#6B4423',
      componentColor: '#8B4513',
      cardBackground: '#FAF0E6',
      pageBackground: '#FFF8F0'
    },
    {
      name: 'Clay Earth',
      category: 'Earth / Desert',
      primaryAccent: '#6D4C41',
      secondaryAccents: ['#795548', '#8D6E63', '#A1887F', '#BCAAA4', '#D7CCC8'],
      headingColor: '#3E2723',
      componentColor: '#4E342E',
      cardBackground: '#EFEBE9',
      pageBackground: '#F9F7F5'
    },
    {
      name: 'Chocolate Truffle',
      category: 'Earth / Desert',
      primaryAccent: '#5D4037',
      secondaryAccents: ['#6D4C41', '#795548', '#8D6E63', '#A1887F', '#BCAAA4'],
      headingColor: '#3E2723',
      componentColor: '#4E342E',
      cardBackground: '#EFEBE9',
      pageBackground: '#F9F7F5'
    },

    // === OCEAN / WATER ===
    {
      name: 'Ocean Breeze',
      category: 'Ocean / Water',
      primaryAccent: '#1E88E5',
      secondaryAccents: ['#0288D1', '#039BE5', '#42A5F5', '#64B5F6', '#90CAF9'],
      headingColor: '#0D47A1',
      componentColor: '#1565C0',
      cardBackground: '#E1F5FE',
      pageBackground: '#F1F9FF'
    },
    {
      name: 'Tropical Paradise',
      category: 'Ocean / Water',
      primaryAccent: '#00ACC1',
      secondaryAccents: ['#00BCD4', '#26C6DA', '#4DD0E1', '#80DEEA', '#B2EBF2'],
      headingColor: '#006064',
      componentColor: '#00838F',
      cardBackground: '#E0F7FA',
      pageBackground: '#F0FCFF'
    },
    {
      name: 'Aquamarine',
      category: 'Ocean / Water',
      primaryAccent: '#00BCD4',
      secondaryAccents: ['#26C6DA', '#4DD0E1', '#80DEEA', '#B2EBF2', '#E0F7FA'],
      headingColor: '#006064',
      componentColor: '#00838F',
      cardBackground: '#E0F7FA',
      pageBackground: '#F0FCFF'
    },
    {
      name: 'Teal Lagoon',
      category: 'Ocean / Water',
      primaryAccent: '#009688',
      secondaryAccents: ['#26A69A', '#4DB6AC', '#80CBC4', '#B2DFDB', '#E0F2F1'],
      headingColor: '#004D40',
      componentColor: '#00695C',
      cardBackground: '#E0F2F1',
      pageBackground: '#F0FAF9'
    },
    {
      name: 'Turquoise Wave',
      category: 'Ocean / Water',
      primaryAccent: '#26A69A',
      secondaryAccents: ['#4DB6AC', '#80CBC4', '#B2DFDB', '#E0F2F1', '#009688'],
      headingColor: '#004D40',
      componentColor: '#00695C',
      cardBackground: '#E0F2F1',
      pageBackground: '#F0FAF9'
    },

    // === FOREST / GREEN ===
    {
      name: 'Forest Green',
      category: 'Forest / Green',
      primaryAccent: '#2E7D32',
      secondaryAccents: ['#388E3C', '#43A047', '#4CAF50', '#66BB6A', '#81C784'],
      headingColor: '#1B5E20',
      componentColor: '#33691E',
      cardBackground: '#E8F5E9',
      pageBackground: '#F1F8F4'
    },
    {
      name: 'Pine Forest',
      category: 'Forest / Green',
      primaryAccent: '#2E7D32',
      secondaryAccents: ['#388E3C', '#43A047', '#4CAF50', '#66BB6A', '#81C784'],
      headingColor: '#1B5E20',
      componentColor: '#33691E',
      cardBackground: '#E8F5E9',
      pageBackground: '#F1F8F4'
    },
    {
      name: 'Moss & Stone',
      category: 'Forest / Green',
      primaryAccent: '#558B2F',
      secondaryAccents: ['#689F38', '#7CB342', '#8BC34A', '#9CCC65', '#AED581'],
      headingColor: '#33691E',
      componentColor: '#558B2F',
      cardBackground: '#F1F8E9',
      pageBackground: '#F9FCF5'
    },
    {
      name: 'Moss Green',
      category: 'Forest / Green',
      primaryAccent: '#689F38',
      secondaryAccents: ['#7CB342', '#8BC34A', '#9CCC65', '#AED581', '#C5E1A5'],
      headingColor: '#33691E',
      componentColor: '#558B2F',
      cardBackground: '#F1F8E9',
      pageBackground: '#F9FCF5'
    },
    {
      name: 'Willow Green',
      category: 'Forest / Green',
      primaryAccent: '#7CB342',
      secondaryAccents: ['#8BC34A', '#9CCC65', '#AED581', '#C5E1A5', '#DCEDC8'],
      headingColor: '#33691E',
      componentColor: '#558B2F',
      cardBackground: '#F1F8E9',
      pageBackground: '#F9FCF5'
    },

    // === ROYAL / LUXE ===
    {
      name: 'Royal Purple',
      category: 'Royal / Luxe',
      primaryAccent: '#7B1FA2',
      secondaryAccents: ['#8E24AA', '#9C27B0', '#AB47BC', '#BA68C8', '#CE93D8'],
      headingColor: '#4A148C',
      componentColor: '#6A1B9A',
      cardBackground: '#F3E5F5',
      pageBackground: '#FAF5FC'
    },
    {
      name: 'Wine Cellar',
      category: 'Royal / Luxe',
      primaryAccent: '#6A1B9A',
      secondaryAccents: ['#7B1FA2', '#8E24AA', '#9C27B0', '#AB47BC', '#BA68C8'],
      headingColor: '#4A148C',
      componentColor: '#6A1B9A',
      cardBackground: '#F3E5F5',
      pageBackground: '#FAF5FC'
    },
    {
      name: 'Amethyst Glow',
      category: 'Royal / Luxe',
      primaryAccent: '#7E57C2',
      secondaryAccents: ['#9575CD', '#B39DDB', '#D1C4E9', '#EDE7F6', '#673AB7'],
      headingColor: '#4527A0',
      componentColor: '#5E35B1',
      cardBackground: '#EDE7F6',
      pageBackground: '#F8F5FF'
    },
    {
      name: 'Plum Perfect',
      category: 'Royal / Luxe',
      primaryAccent: '#8E24AA',
      secondaryAccents: ['#9C27B0', '#AB47BC', '#BA68C8', '#CE93D8', '#E1BEE7'],
      headingColor: '#4A148C',
      componentColor: '#6A1B9A',
      cardBackground: '#F3E5F5',
      pageBackground: '#FAF5FC'
    },
    {
      name: 'Orchid Bloom',
      category: 'Royal / Luxe',
      primaryAccent: '#9C27B0',
      secondaryAccents: ['#AB47BC', '#BA68C8', '#CE93D8', '#E1BEE7', '#F3E5F5'],
      headingColor: '#4A148C',
      componentColor: '#6A1B9A',
      cardBackground: '#F3E5F5',
      pageBackground: '#FAF5FC'
    },

    // === FLORAL / BLOSSOM ===
    {
      name: 'Cherry Blossom',
      category: 'Floral / Blossom',
      primaryAccent: '#F48FB1',
      secondaryAccents: ['#F06292', '#EC407A', '#E91E63', '#F8BBD0', '#FCE4EC'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FFF0F5',
      pageBackground: '#FFF9FB'
    },
    {
      name: 'Rose Garden',
      category: 'Floral / Blossom',
      primaryAccent: '#D81B60',
      secondaryAccents: ['#E91E63', '#F06292', '#F48FB1', '#F8BBD0', '#FCE4EC'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },
    {
      name: 'Blush Pink',
      category: 'Floral / Blossom',
      primaryAccent: '#F8BBD0',
      secondaryAccents: ['#FCE4EC', '#F48FB1', '#F06292', '#EC407A', '#E91E63'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },
    {
      name: 'Dusty Rose',
      category: 'Floral / Blossom',
      primaryAccent: '#CE93D8',
      secondaryAccents: ['#E1BEE7', '#F3E5F5', '#BA68C8', '#AB47BC', '#9C27B0'],
      headingColor: '#4A148C',
      componentColor: '#6A1B9A',
      cardBackground: '#F3E5F5',
      pageBackground: '#FAF5FC'
    },
    {
      name: 'Lilac Dream',
      category: 'Floral / Blossom',
      primaryAccent: '#E1BEE7',
      secondaryAccents: ['#F3E5F5', '#CE93D8', '#BA68C8', '#AB47BC', '#9C27B0'],
      headingColor: '#4A148C',
      componentColor: '#6A1B9A',
      cardBackground: '#F3E5F5',
      pageBackground: '#FAF5FC'
    },

    // === METALLIC / STEEL ===
    {
      name: 'Graphite Steel',
      category: 'Metallic / Steel',
      primaryAccent: '#546E7A',
      secondaryAccents: ['#607D8B', '#78909C', '#90A4AE', '#B0BEC5', '#CFD8DC'],
      headingColor: '#263238',
      componentColor: '#37474F',
      cardBackground: '#ECEFF1',
      pageBackground: '#F5F7F8'
    },
    {
      name: 'Slate Shadow',
      category: 'Metallic / Steel',
      primaryAccent: '#455A64',
      secondaryAccents: ['#546E7A', '#607D8B', '#78909C', '#90A4AE', '#B0BEC5'],
      headingColor: '#263238',
      componentColor: '#37474F',
      cardBackground: '#ECEFF1',
      pageBackground: '#F5F7F8'
    },
    {
      name: 'Steel Blue',
      category: 'Metallic / Steel',
      primaryAccent: '#607D8B',
      secondaryAccents: ['#78909C', '#90A4AE', '#B0BEC5', '#CFD8DC', '#ECEFF1'],
      headingColor: '#263238',
      componentColor: '#37474F',
      cardBackground: '#ECEFF1',
      pageBackground: '#F5F7F8'
    },
    {
      name: 'Peacock Plume',
      category: 'Metallic / Steel',
      primaryAccent: '#00796B',
      secondaryAccents: ['#26A69A', '#009688', '#80CBC4', '#4DB6AC', '#B2DFDB'],
      headingColor: '#004D40',
      componentColor: '#00695C',
      cardBackground: '#E0F2F1',
      pageBackground: '#F0FAF9'
    },
    {
      name: 'Emerald Dream',
      category: 'Metallic / Steel',
      primaryAccent: '#00897B',
      secondaryAccents: ['#009688', '#26A69A', '#4DB6AC', '#80CBC4', '#B2DFDB'],
      headingColor: '#004D40',
      componentColor: '#00695C',
      cardBackground: '#E0F2F1',
      pageBackground: '#F0FAF9'
    },

    // === GOLDEN / HONEY ===
    {
      name: 'Golden Hour',
      category: 'Golden / Honey',
      primaryAccent: '#FFB300',
      secondaryAccents: ['#FFC107', '#FFD54F', '#FFE082', '#FFECB3', '#FFF8E1'],
      headingColor: '#FF6F00',
      componentColor: '#F57C00',
      cardBackground: '#FFF9E6',
      pageBackground: '#FFFCF5'
    },
    {
      name: 'Honey Gold',
      category: 'Golden / Honey',
      primaryAccent: '#FFD54F',
      secondaryAccents: ['#FFE082', '#FFECB3', '#FFF8E1', '#FFCA28', '#FFC107'],
      headingColor: '#FF6F00',
      componentColor: '#F57C00',
      cardBackground: '#FFF8E1',
      pageBackground: '#FFFCF5'
    },
    {
      name: 'Amber Glow',
      category: 'Golden / Honey',
      primaryAccent: '#FFA726',
      secondaryAccents: ['#FFB74D', '#FFCC80', '#FFE0B2', '#FFF3E0', '#FF9800'],
      headingColor: '#E65100',
      componentColor: '#EF6C00',
      cardBackground: '#FFF3E0',
      pageBackground: '#FFFAF5'
    },
    {
      name: 'Marigold',
      category: 'Golden / Honey',
      primaryAccent: '#FFAB00',
      secondaryAccents: ['#FFB300', '#FFC107', '#FFCA28', '#FFD54F', '#FFE082'],
      headingColor: '#FF6F00',
      componentColor: '#F57C00',
      cardBackground: '#FFF8E1',
      pageBackground: '#FFFCF5'
    },
    {
      name: 'Sunshine',
      category: 'Golden / Honey',
      primaryAccent: '#FFC107',
      secondaryAccents: ['#FFCA28', '#FFD54F', '#FFE082', '#FFECB3', '#FFF8E1'],
      headingColor: '#FF6F00',
      componentColor: '#F57C00',
      cardBackground: '#FFF8E1',
      pageBackground: '#FFFCF5'
    },

    // === ARCTIC / ICE ===
    {
      name: 'Arctic Ice',
      category: 'Arctic / Ice',
      primaryAccent: '#B3E5FC',
      secondaryAccents: ['#81D4FA', '#4FC3F7', '#29B6F6', '#03A9F4', '#039BE5'],
      headingColor: '#01579B',
      componentColor: '#0277BD',
      cardBackground: '#E1F5FE',
      pageBackground: '#F0FAFF'
    },
    {
      name: 'Crystal Blue',
      category: 'Arctic / Ice',
      primaryAccent: '#29B6F6',
      secondaryAccents: ['#4FC3F7', '#81D4FA', '#B3E5FC', '#E1F5FE', '#03A9F4'],
      headingColor: '#01579B',
      componentColor: '#0277BD',
      cardBackground: '#E1F5FE',
      pageBackground: '#F0FAFF'
    },
    {
      name: 'Cerulean Blue',
      category: 'Arctic / Ice',
      primaryAccent: '#03A9F4',
      secondaryAccents: ['#29B6F6', '#4FC3F7', '#81D4FA', '#B3E5FC', '#E1F5FE'],
      headingColor: '#01579B',
      componentColor: '#0277BD',
      cardBackground: '#E1F5FE',
      pageBackground: '#F0FAFF'
    },
    {
      name: 'Azure Blue',
      category: 'Arctic / Ice',
      primaryAccent: '#2196F3',
      secondaryAccents: ['#42A5F5', '#64B5F6', '#90CAF9', '#BBDEFB', '#E3F2FD'],
      headingColor: '#0D47A1',
      componentColor: '#1565C0',
      cardBackground: '#E3F2FD',
      pageBackground: '#F0F7FF'
    },
    {
      name: 'Cobalt Deep',
      category: 'Arctic / Ice',
      primaryAccent: '#1565C0',
      secondaryAccents: ['#1976D2', '#1E88E5', '#2196F3', '#42A5F5', '#64B5F6'],
      headingColor: '#0D47A1',
      componentColor: '#1565C0',
      cardBackground: '#E3F2FD',
      pageBackground: '#F0F7FF'
    },

    // === FIRE / CRIMSON ===
    {
      name: 'Crimson Fire',
      category: 'Fire / Crimson',
      primaryAccent: '#C62828',
      secondaryAccents: ['#D32F2F', '#E53935', '#F44336', '#EF5350', '#E57373'],
      headingColor: '#B71C1C',
      componentColor: '#C62828',
      cardBackground: '#FFEBEE',
      pageBackground: '#FFF5F5'
    },
    {
      name: 'Rust & Copper',
      category: 'Fire / Crimson',
      primaryAccent: '#BF360C',
      secondaryAccents: ['#D84315', '#E64A19', '#F4511E', '#FF5722', '#FF7043'],
      headingColor: '#BF360C',
      componentColor: '#D84315',
      cardBackground: '#FBE9E7',
      pageBackground: '#FFF4F1'
    },
    {
      name: 'Coral Reef',
      category: 'Fire / Crimson',
      primaryAccent: '#FF7043',
      secondaryAccents: ['#FF8A65', '#FFAB91', '#FFCCBC', '#FBE9E7', '#FF5722'],
      headingColor: '#BF360C',
      componentColor: '#D84315',
      cardBackground: '#FBE9E7',
      pageBackground: '#FFF4F1'
    },
    {
      name: 'Autumn Harvest',
      category: 'Fire / Crimson',
      primaryAccent: '#D84315',
      secondaryAccents: ['#E64A19', '#F4511E', '#FF5722', '#FF7043', '#FF8A65'],
      headingColor: '#BF360C',
      componentColor: '#D84315',
      cardBackground: '#FBE9E7',
      pageBackground: '#FFF4F1'
    },
    {
      name: 'Peach Sorbet',
      category: 'Fire / Crimson',
      primaryAccent: '#FFAB91',
      secondaryAccents: ['#FFCCBC', '#FBE9E7', '#FF8A65', '#FF7043', '#FF5722'],
      headingColor: '#BF360C',
      componentColor: '#D84315',
      cardBackground: '#FFF0EB',
      pageBackground: '#FFF8F5'
    },

    // === LAVENDER / PURPLE ===
    {
      name: 'Lavender Fields',
      category: 'Lavender / Purple',
      primaryAccent: '#9575CD',
      secondaryAccents: ['#B39DDB', '#D1C4E9', '#EDE7F6', '#7E57C2', '#673AB7'],
      headingColor: '#4527A0',
      componentColor: '#5E35B1',
      cardBackground: '#F3EEFF',
      pageBackground: '#FAF7FF'
    },
    {
      name: 'Mauve Mist',
      category: 'Lavender / Purple',
      primaryAccent: '#BA68C8',
      secondaryAccents: ['#CE93D8', '#E1BEE7', '#F3E5F5', '#AB47BC', '#9C27B0'],
      headingColor: '#4A148C',
      componentColor: '#6A1B9A',
      cardBackground: '#F3E5F5',
      pageBackground: '#FAF5FC'
    },
    {
      name: 'Violet Haze',
      category: 'Lavender / Purple',
      primaryAccent: '#AB47BC',
      secondaryAccents: ['#BA68C8', '#CE93D8', '#E1BEE7', '#F3E5F5', '#9C27B0'],
      headingColor: '#4A148C',
      componentColor: '#6A1B9A',
      cardBackground: '#F3E5F5',
      pageBackground: '#FAF5FC'
    },
    {
      name: 'Electric Purple',
      category: 'Lavender / Purple',
      primaryAccent: '#673AB7',
      secondaryAccents: ['#7E57C2', '#9575CD', '#B39DDB', '#D1C4E9', '#EDE7F6'],
      headingColor: '#4527A0',
      componentColor: '#5E35B1',
      cardBackground: '#EDE7F6',
      pageBackground: '#F8F5FF'
    },
    {
      name: 'Periwinkle',
      category: 'Lavender / Purple',
      primaryAccent: '#9FA8DA',
      secondaryAccents: ['#C5CAE9', '#E8EAF6', '#7986CB', '#5C6BC0', '#3F51B5'],
      headingColor: '#1A237E',
      componentColor: '#283593',
      cardBackground: '#E8EAF6',
      pageBackground: '#F3F4FB'
    },

    // === MINT / FRESH ===
    {
      name: 'Mint Fresh',
      category: 'Mint / Fresh',
      primaryAccent: '#26A69A',
      secondaryAccents: ['#4DB6AC', '#80CBC4', '#B2DFDB', '#E0F2F1', '#009688'],
      headingColor: '#004D40',
      componentColor: '#00695C',
      cardBackground: '#E0F2F1',
      pageBackground: '#F0FAF9'
    },
    {
      name: 'Sage Wisdom',
      category: 'Mint / Fresh',
      primaryAccent: '#7CB342',
      secondaryAccents: ['#8BC34A', '#9CCC65', '#AED581', '#C5E1A5', '#DCEDC8'],
      headingColor: '#33691E',
      componentColor: '#558B2F',
      cardBackground: '#F1F8E9',
      pageBackground: '#F9FCF5'
    },
    {
      name: 'Lime Splash',
      category: 'Mint / Fresh',
      primaryAccent: '#9CCC65',
      secondaryAccents: ['#AED581', '#C5E1A5', '#DCEDC8', '#F1F8E9', '#8BC34A'],
      headingColor: '#33691E',
      componentColor: '#558B2F',
      cardBackground: '#F1F8E9',
      pageBackground: '#F9FCF5'
    },
    {
      name: 'Spring Meadow',
      category: 'Mint / Fresh',
      primaryAccent: '#8BC34A',
      secondaryAccents: ['#9CCC65', '#AED581', '#C5E1A5', '#DCEDC8', '#F1F8E9'],
      headingColor: '#33691E',
      componentColor: '#558B2F',
      cardBackground: '#F1F8E9',
      pageBackground: '#F9FCF5'
    },
    {
      name: 'Apple Green',
      category: 'Mint / Fresh',
      primaryAccent: '#9CCC65',
      secondaryAccents: ['#AED581', '#C5E1A5', '#DCEDC8', '#F1F8E9', '#8BC34A'],
      headingColor: '#33691E',
      componentColor: '#558B2F',
      cardBackground: '#F1F8E9',
      pageBackground: '#F9FCF5'
    },

    // === BERRY / PINK ===
    {
      name: 'Berry Bliss',
      category: 'Berry / Pink',
      primaryAccent: '#AD1457',
      secondaryAccents: ['#C2185B', '#D81B60', '#E91E63', '#EC407A', '#F06292'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },
    {
      name: 'Bubblegum Pop',
      category: 'Berry / Pink',
      primaryAccent: '#F06292',
      secondaryAccents: ['#F48FB1', '#F8BBD0', '#FCE4EC', '#EC407A', '#E91E63'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },
    {
      name: 'Raspberry Ripple',
      category: 'Berry / Pink',
      primaryAccent: '#E91E63',
      secondaryAccents: ['#EC407A', '#F06292', '#F48FB1', '#F8BBD0', '#FCE4EC'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },
    {
      name: 'Fuchsia Flame',
      category: 'Berry / Pink',
      primaryAccent: '#D81B60',
      secondaryAccents: ['#E91E63', '#EC407A', '#F06292', '#F48FB1', '#F8BBD0'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },
    {
      name: 'Magenta Magic',
      category: 'Berry / Pink',
      primaryAccent: '#C2185B',
      secondaryAccents: ['#D81B60', '#E91E63', '#EC407A', '#F06292', '#F48FB1'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },

    // === WOOD / BROWN ===
    {
      name: 'Mahogany',
      category: 'Wood / Brown',
      primaryAccent: '#795548',
      secondaryAccents: ['#8D6E63', '#A1887F', '#BCAAA4', '#D7CCC8', '#EFEBE9'],
      headingColor: '#3E2723',
      componentColor: '#4E342E',
      cardBackground: '#EFEBE9',
      pageBackground: '#F9F7F5'
    },
    {
      name: 'Walnut Wood',
      category: 'Wood / Brown',
      primaryAccent: '#5D4037',
      secondaryAccents: ['#6D4C41', '#795548', '#8D6E63', '#A1887F', '#BCAAA4'],
      headingColor: '#3E2723',
      componentColor: '#4E342E',
      cardBackground: '#EFEBE9',
      pageBackground: '#F9F7F5'
    },
    {
      name: 'Hazelnut',
      category: 'Wood / Brown',
      primaryAccent: '#A1887F',
      secondaryAccents: ['#BCAAA4', '#D7CCC8', '#EFEBE9', '#8D6E63', '#795548'],
      headingColor: '#3E2723',
      componentColor: '#4E342E',
      cardBackground: '#EFEBE9',
      pageBackground: '#F9F7F5'
    },
    {
      name: 'Espresso',
      category: 'Wood / Brown',
      primaryAccent: '#4E342E',
      secondaryAccents: ['#5D4037', '#6D4C41', '#795548', '#8D6E63', '#A1887F'],
      headingColor: '#3E2723',
      componentColor: '#4E342E',
      cardBackground: '#EFEBE9',
      pageBackground: '#F9F7F5'
    },
    {
      name: 'Cinnamon Spice',
      category: 'Wood / Brown',
      primaryAccent: '#8D6E63',
      secondaryAccents: ['#A1887F', '#BCAAA4', '#D7CCC8', '#EFEBE9', '#795548'],
      headingColor: '#3E2723',
      componentColor: '#4E342E',
      cardBackground: '#EFEBE9',
      pageBackground: '#F9F7F5'
    },

    // === LEMON / YELLOW ===
    {
      name: 'Lemon Zest',
      category: 'Lemon / Yellow',
      primaryAccent: '#FDD835',
      secondaryAccents: ['#FFEB3B', '#FFEE58', '#FFF176', '#FFF59D', '#FFF9C4'],
      headingColor: '#F57F17',
      componentColor: '#F9A825',
      cardBackground: '#FFFDE7',
      pageBackground: '#FFFEF5'
    },
    {
      name: 'Canary Yellow',
      category: 'Lemon / Yellow',
      primaryAccent: '#FFEB3B',
      secondaryAccents: ['#FFEE58', '#FFF176', '#FFF59D', '#FFF9C4', '#FFFDE7'],
      headingColor: '#F57F17',
      componentColor: '#F9A825',
      cardBackground: '#FFFDE7',
      pageBackground: '#FFFEF5'
    },
    {
      name: 'Daffodil Bright',
      category: 'Lemon / Yellow',
      primaryAccent: '#FFF176',
      secondaryAccents: ['#FFF59D', '#FFF9C4', '#FFFDE7', '#FFEE58', '#FFEB3B'],
      headingColor: '#F57F17',
      componentColor: '#F9A825',
      cardBackground: '#FFFDE7',
      pageBackground: '#FFFEF5'
    },
    {
      name: 'Buttercup',
      category: 'Lemon / Yellow',
      primaryAccent: '#FFEE58',
      secondaryAccents: ['#FFF176', '#FFF59D', '#FFF9C4', '#FFFDE7', '#FFEB3B'],
      headingColor: '#F57F17',
      componentColor: '#F9A825',
      cardBackground: '#FFFDE7',
      pageBackground: '#FFFEF5'
    },
    {
      name: 'Pineapple',
      category: 'Lemon / Yellow',
      primaryAccent: '#FFCA28',
      secondaryAccents: ['#FFD54F', '#FFE082', '#FFECB3', '#FFF8E1', '#FFC107'],
      headingColor: '#FF6F00',
      componentColor: '#F57C00',
      cardBackground: '#FFF8E1',
      pageBackground: '#FFFCF5'
    },

    // === SAPPHIRE / NAVY ===
    {
      name: 'Sapphire Sky',
      category: 'Sapphire / Navy',
      primaryAccent: '#1976D2',
      secondaryAccents: ['#2196F3', '#42A5F5', '#64B5F6', '#90CAF9', '#BBDEFB'],
      headingColor: '#0D47A1',
      componentColor: '#1565C0',
      cardBackground: '#E3F2FD',
      pageBackground: '#F0F7FF'
    },
    {
      name: 'Navy Commander',
      category: 'Sapphire / Navy',
      primaryAccent: '#0D47A1',
      secondaryAccents: ['#1565C0', '#1976D2', '#1E88E5', '#2196F3', '#42A5F5'],
      headingColor: '#01579B',
      componentColor: '#0277BD',
      cardBackground: '#E3F2FD',
      pageBackground: '#F0F7FF'
    },
    {
      name: 'Pacific Ocean',
      category: 'Sapphire / Navy',
      primaryAccent: '#0277BD',
      secondaryAccents: ['#0288D1', '#039BE5', '#03A9F4', '#29B6F6', '#4FC3F7'],
      headingColor: '#01579B',
      componentColor: '#0277BD',
      cardBackground: '#E1F5FE',
      pageBackground: '#F0FAFF'
    },
    {
      name: 'Deep Sea',
      category: 'Sapphire / Navy',
      primaryAccent: '#006064',
      secondaryAccents: ['#00838F', '#0097A7', '#00ACC1', '#00BCD4', '#26C6DA'],
      headingColor: '#004D40',
      componentColor: '#006064',
      cardBackground: '#E0F7FA',
      pageBackground: '#F0FCFF'
    },
    {
      name: 'Indigo Nights',
      category: 'Sapphire / Navy',
      primaryAccent: '#3F51B5',
      secondaryAccents: ['#5C6BC0', '#7986CB', '#9FA8DA', '#C5CAE9', '#E8EAF6'],
      headingColor: '#1A237E',
      componentColor: '#283593',
      cardBackground: '#E8EAF6',
      pageBackground: '#F3F4FB'
    },

    // === VANILLA / CREAM ===
    {
      name: 'Vanilla Cream',
      category: 'Vanilla / Cream',
      primaryAccent: '#F5E6D3',
      secondaryAccents: ['#FFF8DC', '#FAEBD7', '#FFE4B5', '#FFDEAD', '#F5DEB3'],
      headingColor: '#8B7355',
      componentColor: '#A0826D',
      cardBackground: '#FFFEF9',
      pageBackground: '#FFFFFF'
    },
    {
      name: 'Caramel Swirl',
      category: 'Vanilla / Cream',
      primaryAccent: '#8D6E63',
      secondaryAccents: ['#A1887F', '#BCAAA4', '#D7CCC8', '#EFEBE9', '#795548'],
      headingColor: '#3E2723',
      componentColor: '#4E342E',
      cardBackground: '#EFEBE9',
      pageBackground: '#F9F7F5'
    },
    {
      name: 'Chestnut',
      category: 'Vanilla / Cream',
      primaryAccent: '#BCAAA4',
      secondaryAccents: ['#D7CCC8', '#EFEBE9', '#A1887F', '#8D6E63', '#795548'],
      headingColor: '#3E2723',
      componentColor: '#4E342E',
      cardBackground: '#EFEBE9',
      pageBackground: '#F9F7F5'
    },
    {
      name: 'Golden Wheat',
      category: 'Vanilla / Cream',
      primaryAccent: '#FFD54F',
      secondaryAccents: ['#FFE082', '#FFECB3', '#FFF8E1', '#FFCA28', '#FFC107'],
      headingColor: '#FF6F00',
      componentColor: '#F57C00',
      cardBackground: '#FFF8E1',
      pageBackground: '#FFFCF5'
    },
    {
      name: 'Olive Grove',
      category: 'Vanilla / Cream',
      primaryAccent: '#689F38',
      secondaryAccents: ['#7CB342', '#8BC34A', '#9CCC65', '#AED581', '#C5E1A5'],
      headingColor: '#33691E',
      componentColor: '#558B2F',
      cardBackground: '#F1F8E9',
      pageBackground: '#F9FCF5'
    },

    // === TROPICAL / FRUIT ===
    {
      name: 'Watermelon',
      category: 'Tropical / Fruit',
      primaryAccent: '#F06292',
      secondaryAccents: ['#F48FB1', '#F8BBD0', '#FCE4EC', '#EC407A', '#E91E63'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },
    {
      name: 'Pink Lemonade',
      category: 'Tropical / Fruit',
      primaryAccent: '#F48FB1',
      secondaryAccents: ['#F8BBD0', '#FCE4EC', '#F06292', '#EC407A', '#E91E63'],
      headingColor: '#880E4F',
      componentColor: '#AD1457',
      cardBackground: '#FCE4EC',
      pageBackground: '#FFF0F5'
    },
    {
      name: 'Bamboo',
      category: 'Tropical / Fruit',
      primaryAccent: '#AED581',
      secondaryAccents: ['#C5E1A5', '#DCEDC8', '#F1F8E9', '#9CCC65', '#8BC34A'],
      headingColor: '#33691E',
      componentColor: '#558B2F',
      cardBackground: '#F1F8E9',
      pageBackground: '#F9FCF5'
    },
    {
      name: 'Fresh Grass',
      category: 'Tropical / Fruit',
      primaryAccent: '#66BB6A',
      secondaryAccents: ['#81C784', '#A5D6A7', '#C8E6C9', '#E8F5E9', '#4CAF50'],
      headingColor: '#1B5E20',
      componentColor: '#2E7D32',
      cardBackground: '#E8F5E9',
      pageBackground: '#F1F8F4'
    },
    {
      name: 'Iris Purple',
      category: 'Tropical / Fruit',
      primaryAccent: '#5C6BC0',
      secondaryAccents: ['#7986CB', '#9FA8DA', '#C5CAE9', '#E8EAF6', '#3F51B5'],
      headingColor: '#1A237E',
      componentColor: '#283593',
      cardBackground: '#E8EAF6',
      pageBackground: '#F3F4FB'
    },

    // === DARK / NIGHT THEMES ===
    {
      name: 'Midnight Eclipse',
      category: 'Dark / Night',
      primaryAccent: '#BB86FC',
      secondaryAccents: ['#CF94FF', '#E0B3FF', '#F0D5FF', '#9965F4', '#7C4DFF'],
      headingColor: '#E0E0E0',
      componentColor: '#B0B0B0',
      cardBackground: '#1E1E2E',
      pageBackground: '#0F0F1A'
    },
    {
      name: 'Obsidian Night',
      category: 'Dark / Night',
      primaryAccent: '#00D4FF',
      secondaryAccents: ['#33DDFF', '#66E6FF', '#99EEFF', '#00BFE8', '#00A8D0'],
      headingColor: '#F0F0F0',
      componentColor: '#C5C5C5',
      cardBackground: '#1A1A1A',
      pageBackground: '#0D0D0D'
    },
    {
      name: 'Dark Steel',
      category: 'Dark / Night',
      primaryAccent: '#FFD700',
      secondaryAccents: ['#FFDD33', '#FFE566', '#FFED99', '#FFC700', '#FFB700'],
      headingColor: '#E8E8E8',
      componentColor: '#BEBEBE',
      cardBackground: '#2A2A3E',
      pageBackground: '#1A1A2E'
    },
    {
      name: 'Carbon Black',
      category: 'Dark / Night',
      primaryAccent: '#FF4081',
      secondaryAccents: ['#FF5A92', '#FF75A3', '#FF8FB5', '#E91E63', '#C2185B'],
      headingColor: '#EFEFEF',
      componentColor: '#C0C0C0',
      cardBackground: '#1C1C28',
      pageBackground: '#0E0E14'
    },
    {
      name: 'Slate Shadow',
      category: 'Dark / Night',
      primaryAccent: '#4ECDC4',
      secondaryAccents: ['#6DD5CE', '#8DDDD8', '#ACE5E2', '#3FB8AF', '#2FA49C'],
      headingColor: '#E5E5E5',
      componentColor: '#B8B8B8',
      cardBackground: '#25252E',
      pageBackground: '#16161D'
    },

    // === MYSTICAL / MAGIC ===
    {
      name: 'Mystic Violet',
      category: 'Mystical / Magic',
      primaryAccent: '#A78BFA',
      secondaryAccents: ['#B89FFB', '#C9B3FC', '#DAC7FD', '#9777EA', '#8763E8'],
      headingColor: '#F0E6FF',
      componentColor: '#D0C0E8',
      cardBackground: '#1F1B2E',
      pageBackground: '#13101E'
    },
    {
      name: 'Arcane Purple',
      category: 'Mystical / Magic',
      primaryAccent: '#DA70D6',
      secondaryAccents: ['#E085DB', '#E799E0', '#EDADE5', '#C95CD1', '#B84BBB'],
      headingColor: '#FFE5FF',
      componentColor: '#E8D0E8',
      cardBackground: '#2E1A2E',
      pageBackground: '#1A0F1A'
    },
    {
      name: 'Enchanted Mist',
      category: 'Mystical / Magic',
      primaryAccent: '#7DD3C0',
      secondaryAccents: ['#94DAC9', '#AAE1D3', '#C1E8DC', '#66CCAA', '#4FC19C'],
      headingColor: '#E0FFF8',
      componentColor: '#C0E8DD',
      cardBackground: '#1A2E2A',
      pageBackground: '#0F1A17'
    },
    {
      name: 'Wizard Blue',
      category: 'Mystical / Magic',
      primaryAccent: '#5DADE2',
      secondaryAccents: ['#7BBEE8', '#99CEEE', '#B7DFF4', '#3F9BD8', '#2189CE'],
      headingColor: '#E0F0FF',
      componentColor: '#C0D8F0',
      cardBackground: '#1A2533',
      pageBackground: '#0F1520'
    },
    {
      name: 'Magic Hour',
      category: 'Mystical / Magic',
      primaryAccent: '#FF6B9D',
      secondaryAccents: ['#FF85AB', '#FF9FBA', '#FFB9C8', '#FF5189', '#FF3775'],
      headingColor: '#FFE0EC',
      componentColor: '#F0C0D8',
      cardBackground: '#2E1A25',
      pageBackground: '#1A0F15'
    },

    // === COSMIC / SPACE ===
    {
      name: 'Deep Space',
      category: 'Cosmic / Space',
      primaryAccent: '#4A90E2',
      secondaryAccents: ['#6AA3E8', '#8AB6EE', '#AAC9F4', '#2A7DD8', '#0A6ACE'],
      headingColor: '#E0ECFF',
      componentColor: '#C0D5F0',
      cardBackground: '#151B2E',
      pageBackground: '#0A0F1A'
    },
    {
      name: 'Nebula',
      category: 'Cosmic / Space',
      primaryAccent: '#B794F6',
      secondaryAccents: ['#C5A7F8', '#D3BAFA', '#E1CDFC', '#A981F4', '#9B6EF2'],
      headingColor: '#F0E8FF',
      componentColor: '#D8D0E8',
      cardBackground: '#1F1A2E',
      pageBackground: '#120F1A'
    },
    {
      name: 'Stardust',
      category: 'Cosmic / Space',
      primaryAccent: '#FFD700',
      secondaryAccents: ['#FFDD33', '#FFE566', '#FFED99', '#FFC700', '#FFB700'],
      headingColor: '#FFFACD',
      componentColor: '#F0E6B8',
      cardBackground: '#1A1A0F',
      pageBackground: '#0F0F08'
    },
    {
      name: 'Galactic Purple',
      category: 'Cosmic / Space',
      primaryAccent: '#9D4EDD',
      secondaryAccents: ['#B168E3', '#C582E9', '#D99CEF', '#8934D5', '#751ACB'],
      headingColor: '#F8E8FF',
      componentColor: '#E0D0F0',
      cardBackground: '#1E1525',
      pageBackground: '#12091A'
    },
    {
      name: 'Cosmic Teal',
      category: 'Cosmic / Space',
      primaryAccent: '#00D9FF',
      secondaryAccents: ['#33E0FF', '#66E8FF', '#99EFFF', '#00C4E6', '#00AFCC'],
      headingColor: '#E0F8FF',
      componentColor: '#C0E8F8',
      cardBackground: '#0F1E25',
      pageBackground: '#08121A'
    },

    // === DEEP OCEAN ===
    {
      name: 'Abyss',
      category: 'Deep Ocean',
      primaryAccent: '#00CED1',
      secondaryAccents: ['#20D6D9', '#40DEE1', '#60E6E9', '#00B8BB', '#00A2A5'],
      headingColor: '#E0FFFF',
      componentColor: '#C0F0F0',
      cardBackground: '#0A1A1E',
      pageBackground: '#050F12'
    },
    {
      name: 'Midnight Waters',
      category: 'Deep Ocean',
      primaryAccent: '#1E90FF',
      secondaryAccents: ['#3FA0FF', '#60B0FF', '#81C0FF', '#0080FF', '#0070E0'],
      headingColor: '#E0F0FF',
      componentColor: '#C0D8F0',
      cardBackground: '#0F1820',
      pageBackground: '#080C15'
    },
    {
      name: 'Deep Sea',
      category: 'Deep Ocean',
      primaryAccent: '#00FFD4',
      secondaryAccents: ['#33FFD9', '#66FFDE', '#99FFE3', '#00E6C0', '#00CCAC'],
      headingColor: '#E0FFF8',
      componentColor: '#C0F0E8',
      cardBackground: '#0A1E1A',
      pageBackground: '#05120F'
    },
    {
      name: 'Oceanic Depth',
      category: 'Deep Ocean',
      primaryAccent: '#4DB8E8',
      secondaryAccents: ['#6DC5ED', '#8DD2F2', '#ADDFF7', '#2DABDC', '#0D9ED0'],
      headingColor: '#E8F8FF',
      componentColor: '#D0E8F8',
      cardBackground: '#0F1D2E',
      pageBackground: '#08111A'
    },
    {
      name: 'Bioluminescent',
      category: 'Deep Ocean',
      primaryAccent: '#00FFA3',
      secondaryAccents: ['#33FFB3', '#66FFC3', '#99FFD3', '#00E693', '#00CC83'],
      headingColor: '#E0FFEF',
      componentColor: '#C0F0DD',
      cardBackground: '#0A1E15',
      pageBackground: '#05120A'
    },

    // === GOTHIC / MYSTERY ===
    {
      name: 'Gothic Rose',
      category: 'Gothic / Mystery',
      primaryAccent: '#E74C3C',
      secondaryAccents: ['#EC6658', '#F18075', '#F69A91', '#DC322F', '#C11828'],
      headingColor: '#FFE0DD',
      componentColor: '#F0C8C5',
      cardBackground: '#1E0F0F',
      pageBackground: '#120808'
    },
    {
      name: 'Crimson Shadow',
      category: 'Gothic / Mystery',
      primaryAccent: '#DC143C',
      secondaryAccents: ['#E23456', '#E85470', '#EE748A', '#C60028', '#B00020'],
      headingColor: '#FFE0E8',
      componentColor: '#F0C8D5',
      cardBackground: '#1E0A0F',
      pageBackground: '#120508'
    },
    {
      name: 'Dark Burgundy',
      category: 'Gothic / Mystery',
      primaryAccent: '#8B0000',
      secondaryAccents: ['#A01010', '#B52020', '#CA3030', '#760000', '#610000'],
      headingColor: '#FFD0D0',
      componentColor: '#E8B0B0',
      cardBackground: '#1A0A0A',
      pageBackground: '#0F0505'
    },
    {
      name: 'Midnight Plum',
      category: 'Gothic / Mystery',
      primaryAccent: '#8E44AD',
      secondaryAccents: ['#A25EBD', '#B678CD', '#CA92DD', '#7A2A9D', '#66108D'],
      headingColor: '#F0E0F8',
      componentColor: '#D8C8E8',
      cardBackground: '#1A0F1E',
      pageBackground: '#0F0812'
    },
    {
      name: 'Raven Black',
      category: 'Gothic / Mystery',
      primaryAccent: '#9B59B6',
      secondaryAccents: ['#AF73C6', '#C38DD6', '#D7A7E6', '#873FA6', '#732596'],
      headingColor: '#F8E8FF',
      componentColor: '#E0D0F0',
      cardBackground: '#1E1525',
      pageBackground: '#12091A'
    },

    // === ENCHANTED FOREST ===
    {
      name: 'Dark Forest',
      category: 'Enchanted Forest',
      primaryAccent: '#00FF7F',
      secondaryAccents: ['#33FF92', '#66FFA5', '#99FFB8', '#00E673', '#00CC66'],
      headingColor: '#E0FFEF',
      componentColor: '#C0F0DD',
      cardBackground: '#0A1E12',
      pageBackground: '#05120A'
    },
    {
      name: 'Moss Shadows',
      category: 'Enchanted Forest',
      primaryAccent: '#32CD32',
      secondaryAccents: ['#52D752', '#72E172', '#92EB92', '#28B928', '#1EA51E'],
      headingColor: '#E8FFE8',
      componentColor: '#D0F0D0',
      cardBackground: '#0F1E0F',
      pageBackground: '#081208'
    },
    {
      name: 'Twilight Grove',
      category: 'Enchanted Forest',
      primaryAccent: '#7FFF00',
      secondaryAccents: ['#99FF33', '#B3FF66', '#CCFF99', '#6BE600', '#57CC00'],
      headingColor: '#F0FFE0',
      componentColor: '#D8F0C8',
      cardBackground: '#121E0A',
      pageBackground: '#0A1205'
    },
    {
      name: 'Emerald Night',
      category: 'Enchanted Forest',
      primaryAccent: '#50C878',
      secondaryAccents: ['#6DD28F', '#8ADCA6', '#A7E6BD', '#33BE65', '#16B452'],
      headingColor: '#E0F8E8',
      componentColor: '#C8E8D8',
      cardBackground: '#0A1E15',
      pageBackground: '#05120A'
    },
    {
      name: 'Fern Glade',
      category: 'Enchanted Forest',
      primaryAccent: '#3CB371',
      secondaryAccents: ['#59C385', '#76D399', '#93E3AD', '#1FA355', '#029339'],
      headingColor: '#E8F8F0',
      componentColor: '#D0E8DD',
      cardBackground: '#0A1E12',
      pageBackground: '#05120A'
    },

    // === TWILIGHT / DUSK ===
    {
      name: 'Dusk Horizon',
      category: 'Twilight / Dusk',
      primaryAccent: '#FF6B35',
      secondaryAccents: ['#FF8555', '#FF9F75', '#FFB995', '#E65120', '#CC370A'],
      headingColor: '#FFE8DD',
      componentColor: '#F0D0C5',
      cardBackground: '#1E1210',
      pageBackground: '#120805'
    },
    {
      name: 'Sunset Shadows',
      category: 'Twilight / Dusk',
      primaryAccent: '#FF7F50',
      secondaryAccents: ['#FF9270', '#FFA590', '#FFB8B0', '#FF6535', '#FF4C1A'],
      headingColor: '#FFF0E8',
      componentColor: '#F0D8D0',
      cardBackground: '#1E150F',
      pageBackground: '#120A08'
    },
    {
      name: 'Amber Twilight',
      category: 'Twilight / Dusk',
      primaryAccent: '#FFB347',
      secondaryAccents: ['#FFC067', '#FFCD87', '#FFDAA7', '#FFA027', '#FF8D07'],
      headingColor: '#FFF8E8',
      componentColor: '#F0E8D0',
      cardBackground: '#1E1A0F',
      pageBackground: '#120F08'
    },
    {
      name: 'Purple Dusk',
      category: 'Twilight / Dusk',
      primaryAccent: '#9370DB',
      secondaryAccents: ['#A88AE1', '#BDA4E7', '#D2BEED', '#7E56D3', '#693CC9'],
      headingColor: '#F0E8F8',
      componentColor: '#D8D0E8',
      cardBackground: '#1A121E',
      pageBackground: '#0F0812'
    },
    {
      name: 'Indigo Evening',
      category: 'Twilight / Dusk',
      primaryAccent: '#6A5ACD',
      secondaryAccents: ['#8474D7', '#9E8EE1', '#B8A8EB', '#5040C3', '#3626B9'],
      headingColor: '#E8E0F8',
      componentColor: '#D0C8E8',
      cardBackground: '#15121E',
      pageBackground: '#0A0812'
    },

    // === SHADOWS / NOIR ===
    {
      name: 'Noir',
      category: 'Shadows / Noir',
      primaryAccent: '#C0C0C0',
      secondaryAccents: ['#D0D0D0', '#E0E0E0', '#F0F0F0', '#B0B0B0', '#A0A0A0'],
      headingColor: '#F8F8F8',
      componentColor: '#D8D8D8',
      cardBackground: '#1A1A1A',
      pageBackground: '#0D0D0D'
    },
    {
      name: 'Charcoal Mist',
      category: 'Shadows / Noir',
      primaryAccent: '#A9A9A9',
      secondaryAccents: ['#BABABA', '#CBCBCB', '#DCDCDC', '#989898', '#878787'],
      headingColor: '#F0F0F0',
      componentColor: '#C8C8C8',
      cardBackground: '#1E1E1E',
      pageBackground: '#121212'
    },
    {
      name: 'Smoke Screen',
      category: 'Shadows / Noir',
      primaryAccent: '#778899',
      secondaryAccents: ['#919DAB', '#ABB1BD', '#C5C5CF', '#5D6E81', '#435273'],
      headingColor: '#E8ECF0',
      componentColor: '#D0D4D8',
      cardBackground: '#1A1D22',
      pageBackground: '#0F1115'
    },
    {
      name: 'Pewter Shadow',
      category: 'Shadows / Noir',
      primaryAccent: '#96A8B0',
      secondaryAccents: ['#AAB9C0', '#BECAD0', '#D2DBE0', '#8297A0', '#6E8690'],
      headingColor: '#E8F0F4',
      componentColor: '#D0D8DC',
      cardBackground: '#151A1E',
      pageBackground: '#0A0F12'
    },
    {
      name: 'Eclipse Gray',
      category: 'Shadows / Noir',
      primaryAccent: '#B0C4DE',
      secondaryAccents: ['#C2D2E4', '#D4E0EA', '#E6EEF0', '#9EB6D6', '#8CA8CE'],
      headingColor: '#F0F4F8',
      componentColor: '#D8DCE0',
      cardBackground: '#151820',
      pageBackground: '#0A0C10'
    },

    // === ADDITIONAL DARK MYSTICAL ===
    {
      name: 'Dragon Scales',
      category: 'Mystical / Magic',
      primaryAccent: '#00FF41',
      secondaryAccents: ['#33FF61', '#66FF81', '#99FFA1', '#00E63A', '#00CC33'],
      headingColor: '#E0FFEA',
      componentColor: '#C0F0D5',
      cardBackground: '#0A1E10',
      pageBackground: '#05120A'
    },
    {
      name: 'Phoenix Fire',
      category: 'Mystical / Magic',
      primaryAccent: '#FF4500',
      secondaryAccents: ['#FF5F20', '#FF7940', '#FF9360', '#E63E00', '#CC3700'],
      headingColor: '#FFE0D0',
      componentColor: '#F0C8B8',
      cardBackground: '#1E0F0A',
      pageBackground: '#120805'
    },
    {
      name: 'Lunar Silver',
      category: 'Cosmic / Space',
      primaryAccent: '#E6E6FA',
      secondaryAccents: ['#EBEBFB', '#F0F0FC', '#F5F5FD', '#E1E1F9', '#DCDCF8'],
      headingColor: '#FAFAFF',
      componentColor: '#E0E0F0',
      cardBackground: '#1A1A2E',
      pageBackground: '#0F0F1A'
    },
    {
      name: 'Aurora Borealis',
      category: 'Cosmic / Space',
      primaryAccent: '#00FFAA',
      secondaryAccents: ['#33FFBA', '#66FFCA', '#99FFDA', '#00E699', '#00CC88'],
      headingColor: '#E0FFF5',
      componentColor: '#C0F0E3',
      cardBackground: '#0A1E1A',
      pageBackground: '#05120F'
    },
    {
      name: 'Vampire Night',
      category: 'Gothic / Mystery',
      primaryAccent: '#8B0000',
      secondaryAccents: ['#A01010', '#B52020', '#CA3030', '#760000', '#610000'],
      headingColor: '#FFD0D0',
      componentColor: '#E8B0B0',
      cardBackground: '#1A0505',
      pageBackground: '#0F0202'
    }
  ];

  constructor() {
    // Load saved palette from localStorage on service initialization
    this.loadSavedPalette();
  }

  /************************************************************/
  /** Basic Palette Retrieval Methods **/
  /************************************************************/

  getPalettes(): ColorPalette[] {
    return this.palettes;
  }

  getPaletteByName(name: string): ColorPalette | undefined {
    return this.palettes.find(palette => palette.name.toLowerCase() === name.toLowerCase());
  }

  getPaletteByIndex(index: number): ColorPalette | undefined {
    return this.palettes[index];
  }

  getRandomPalette(): ColorPalette {
    const randomIndex = Math.floor(Math.random() * this.palettes.length);
    return this.palettes[randomIndex];
  }

  getPalettesByCategory(category?: string): ColorPalette[] | { [category: string]: ColorPalette[] } {
    if (category) {
      return this.palettes.filter(palette => palette.category.toLowerCase() === category.toLowerCase());
    }

    // Return all palettes grouped by category
    const grouped: { [category: string]: ColorPalette[] } = {};
    this.palettes.forEach(palette => {
      if (!grouped[palette.category]) {
        grouped[palette.category] = [];
      }
      grouped[palette.category].push(palette);
    });
    return grouped;
  }

  getCategoryNames(): string[] {
    const categories = new Set(this.palettes.map(p => p.category));
    return Array.from(categories);
  }

  /************************************************************/
  /** Current Palette Management **/
  /************************************************************/

  /**
   * Get the current selected palette as an Observable
   * Components can subscribe to this to react to palette changes
   */
  getCurrentPalette$(): Observable<ColorPalette | null> {
    return this.currentPalette$.asObservable();
  }

  /**
   * Get the current selected palette (synchronous)
   */
  getCurrentPalette(): ColorPalette | null {
    return this.currentPalette$.value;
  }

  /**
   * Set the current palette and save to localStorage
   */
  setCurrentPalette(palette: ColorPalette | null): void {
    this.currentPalette$.next(palette);
    this.savePaletteToLocalStorage(palette);
  }

  /**
   * Check if a palette is currently selected
   */
  hasPalette(): boolean {
    return this.currentPalette$.value !== null;
  }

  /************************************************************/
  /** LocalStorage Persistence **/
  /************************************************************/

  /**
   * Save the selected palette to localStorage
   */
  private savePaletteToLocalStorage(palette: ColorPalette | null): void {
    try {
      const paletteName = palette ? palette.name : '';
      localStorage.setItem('selectedColorPalette', paletteName);
      console.log(`[palette-service] Saved palette: ${paletteName || '(none)'}`);
    } catch (error) {
      console.error('[palette-service] Failed to save palette to localStorage:', error);
    }
  }

  /**
   * Load the saved palette from localStorage
   */
  private loadSavedPalette(): void {
    try {
      const savedPaletteName = localStorage.getItem('selectedColorPalette');
      if (savedPaletteName) {
        const palette = this.getPaletteByName(savedPaletteName);
        if (palette) {
          this.currentPalette$.next(palette);
          console.log(`[palette-service] Loaded saved palette: ${savedPaletteName}`);
        } else {
          console.warn(`[palette-service] Saved palette "${savedPaletteName}" not found`);
        }
      }
    } catch (error) {
      console.error('[palette-service] Failed to load palette from localStorage:', error);
    }
  }

  /**
   * Clear the saved palette selection
   */
  clearSavedPalette(): void {
    this.currentPalette$.next(null);
    try {
      localStorage.removeItem('selectedColorPalette');
      console.log('[palette-service] Cleared saved palette');
    } catch (error) {
      console.error('[palette-service] Failed to clear palette from localStorage:', error);
    }
  }

  /************************************************************/
  /** Color Conversion Helpers **/
  /************************************************************/

  /**
   * Get a color from the current palette in decimal format (for scene data storage)
   * @param colorType - The type of color to get (e.g., 'componentColor', 'headingColor', 'primaryAccent')
   * @returns Decimal color value as string, or null if no palette selected
   */
  getCurrentColorDecimal(colorType: keyof ColorPalette): string | null {
    const palette = this.currentPalette$.value;
    if (!palette) {
      return null;
    }

    const hexColor = palette[colorType];
    if (typeof hexColor === 'string' && hexColor.startsWith('#')) {
      const decimal = Lib.HexToDecimal(hexColor);
      return String(decimal);
    }

    return null;
  }

  /**
   * Get a color from the current palette in hex format (for UI display)
   * @param colorType - The type of color to get (e.g., 'componentColor', 'headingColor', 'primaryAccent')
   * @returns Hex color value, or null if no palette selected
   */
  getCurrentColorHex(colorType: keyof ColorPalette): string | null {
    const palette = this.currentPalette$.value;
    if (!palette) {
      return null;
    }

    const color = palette[colorType];
    if (typeof color === 'string') {
      return color;
    }

    return null;
  }

  /**
   * Convert any hex color to decimal format
   * @param hexColor - Hex color string (e.g., '#FFFFFF')
   * @returns Decimal color value as string
   */
  hexToDecimal(hexColor: string): string {
    const decimal = Lib.HexToDecimal(hexColor);
    return String(decimal);
  }

  /**
   * Convert any decimal color to hex format
   * @param decimalColor - Decimal color value (number or string)
   * @returns Hex color string (e.g., '#FFFFFF')
   */
  decimalToHex(decimalColor: number | string): string {
    return Lib.DecimalToHex(decimalColor);
  }

  /************************************************************/
  /** Convenience Methods for Common Use Cases **/
  /************************************************************/

  /**
   * Get the current text/body color in decimal format
   * Commonly used for label text colors
   */
  getCurrentTextColorDecimal(): string | null {
    return this.getCurrentColorDecimal('componentColor');
  }

  /**
   * Get the current heading color in decimal format
   * Commonly used for title/heading text colors
   */
  getCurrentHeadingColorDecimal(): string | null {
    return this.getCurrentColorDecimal('headingColor');
  }

  /**
   * Get the current primary accent color in decimal format
   * Commonly used for borders, highlights, and accents
   */
  getCurrentAccentColorDecimal(): string | null {
    return this.getCurrentColorDecimal('primaryAccent');
  }

  /**
   * Get all current palette colors in decimal format
   * Returns an object with all color properties converted to decimal
   */
  getAllColorsDecimal(): {
    primaryAccent: string | null;
    headingColor: string | null;
    componentColor: string | null;
    cardBackground: string | null;
    pageBackground: string | null;
  } | null {
    const palette = this.currentPalette$.value;
    if (!palette) {
      return null;
    }

    return {
      primaryAccent: this.getCurrentColorDecimal('primaryAccent'),
      headingColor: this.getCurrentColorDecimal('headingColor'),
      componentColor: this.getCurrentColorDecimal('componentColor'),
      cardBackground: this.getCurrentColorDecimal('cardBackground'),
      pageBackground: this.getCurrentColorDecimal('pageBackground')
    };
  }

  /**
   * Get a secondary accent color by index in hex format
   * @param index - Index of the secondary accent (0-4)
   * @returns Hex color string or null if palette doesn't exist or index is invalid
   */
  getSecondaryAccentByIndex(index: number): string | null {
    const palette = this.currentPalette$.value;
    if (!palette || !palette.secondaryAccents || index < 0 || index >= palette.secondaryAccents.length) {
      return null;
    }
    return palette.secondaryAccents[index];
  }

  /**
   * Get a secondary accent color by index in decimal format
   * @param index - Index of the secondary accent (0-4)
   * @returns Decimal color string or null if palette doesn't exist or index is invalid
   */
  getSecondaryAccentDecimalByIndex(index: number): string | null {
    const hexColor = this.getSecondaryAccentByIndex(index);
    if (!hexColor) {
      return null;
    }
    return this.hexToDecimal(hexColor);
  }

  /**
   * Get the appropriate color for a header label type in decimal format
   * Maps label types to specific secondary accent colors:
   * - 'head' -> secondaryAccents[0]
   * - 'title' -> secondaryAccents[1]
   * - 'label' -> secondaryAccents[2]
   * @param labelType - Type of label ('head', 'title', or 'label')
   * @returns Decimal color string or null if palette doesn't exist
   */
  getColorForLabelType(labelType: 'head' | 'title' | 'label'): string | null {
    const indexMap = {
      'head': 0,
      'title': 1,
      'label': 2
    };

    const index = indexMap[labelType];
    return this.getSecondaryAccentDecimalByIndex(index);
  }

  /************************************************************/
  /** Scene Component Application Methods **/
  /************************************************************/

  /**
   * Apply the current palette to all label components in a scene
   * @param players - Array of scene blocks/players
   * @param sceneId - The ID of the scene being updated
   * @returns Array of ISceneItemsUpdate objects for store dispatch
   */
  applyToSceneLabels(players: ISceneBlock[], sceneId: Id): ISceneItemsUpdate[] {
    const palette = this.currentPalette$.value;
    if (!palette || !players || players.length === 0) {
      return [];
    }

    // Get primaryAccent color in decimal format for label color
    const labelColorDecimal = this.hexToDecimal(palette.primaryAccent);

    const updatedItems: ISceneItemsUpdate[] = [];

    // Iterate through all players in the scene
    players.forEach((player) => {
      const playerId = player.$.player;
      const playerItemId = player.$.id;

      // Check if this is a Label component (id: '3241')
      if (playerId === '3241') {
        updatedItems.push({
          sceneId: sceneId,
          sceneItemId: playerItemId,
          values: [{
            key: 'fontColor',
            value: labelColorDecimal,
            storePath: 'Data[0].Label[0].Font[0].$'
          }]
        });
      }
      // Check if this is a LabelQueue component (id: '3242')
      else if (playerId === '3242') {
        updatedItems.push({
          sceneId: sceneId,
          sceneItemId: playerItemId,
          values: [{
            key: 'fontColor',
            value: labelColorDecimal,
            storePath: 'Data[0].LabelQueue[0].Font[0].$'
          }]
        });
      }
    });

    console.log(`[palette-service] Prepared ${updatedItems.length} label component(s) for palette "${palette.name}"`);
    return updatedItems;
  }

  /**
   * Apply a specific palette to scene labels (convenience method)
   * @param palette - The palette to apply
   * @param players - Array of scene blocks/players
   * @param sceneId - The ID of the scene being updated
   * @returns Array of ISceneItemsUpdate objects for store dispatch
   */
  applyPaletteToSceneLabels(palette: ColorPalette, players: ISceneBlock[], sceneId: Id): ISceneItemsUpdate[] {
    // Set as current palette and save
    this.setCurrentPalette(palette);

    // Apply to labels
    return this.applyToSceneLabels(players, sceneId);
  }

  /**
   * Apply the current palette to all button components in a scene
   * Uses headingColor for button font color
   * @param players - Array of scene blocks/players
   * @param sceneId - The ID of the scene being updated
   * @returns Array of ISceneItemsUpdate objects for store dispatch
   */
  applyToSceneButtons(players: ISceneBlock[], sceneId: Id): ISceneItemsUpdate[] {
    const palette = this.currentPalette$.value;
    if (!palette || !players || players.length === 0) {
      return [];
    }

    // Get headingColor in decimal format for button color
    const buttonColorDecimal = this.hexToDecimal(palette.headingColor);

    const updatedItems: ISceneItemsUpdate[] = [];

    // Iterate through all players in the scene
    players.forEach((player) => {
      const playerId = player.$.player;
      const playerItemId = player.$.id;

      // Check if this is a Button component (id: '7000')
      if (playerId === '7000') {
        updatedItems.push({
          sceneId: sceneId,
          sceneItemId: playerItemId,
          values: [{
            key: 'fontColor',
            value: buttonColorDecimal,
            storePath: 'Data[0].Button[0].Font[0].$'
          }]
        });
      }
    });

    console.log(`[palette-service] Prepared ${updatedItems.length} button component(s) for palette "${palette.name}"`);
    return updatedItems;
  }

  /**
   * Apply a specific palette to scene buttons (convenience method)
   * @param palette - The palette to apply
   * @param players - Array of scene blocks/players
   * @param sceneId - The ID of the scene being updated
   * @returns Array of ISceneItemsUpdate objects for store dispatch
   */
  applyPaletteToSceneButtons(palette: ColorPalette, players: ISceneBlock[], sceneId: Id): ISceneItemsUpdate[] {
    // Set as current palette and save
    this.setCurrentPalette(palette);

    // Apply to buttons
    return this.applyToSceneButtons(players, sceneId);
  }

  /**
   * Apply the current palette to all clock components in a scene
   * @param players - Array of scene blocks/players
   * @param sceneId - The ID of the scene being updated
   * @returns Array of ISceneItemsUpdate objects for store dispatch
   */
  applyToSceneClocks(players: ISceneBlock[], sceneId: Id): ISceneItemsUpdate[] {
    const palette = this.currentPalette$.value;
    if (!palette || !players || players.length === 0) {
      return [];
    }

    // Get componentColor in decimal format for clock color
    const clockColorDecimal = this.hexToDecimal(palette.componentColor);

    const updatedItems: ISceneItemsUpdate[] = [];

    // Iterate through all players in the scene
    players.forEach((player) => {
      const playerId = player.$.player;
      const playerItemId = player.$.id;

      // Check if this is a Clock component (id: '3320')
      if (playerId === '3320') {
        updatedItems.push({
          sceneId: sceneId,
          sceneItemId: playerItemId,
          values: [{
            key: 'fontColor',
            value: clockColorDecimal,
            storePath: 'Data[0].Clock[0].Font[0].$'
          }]
        });
      }
    });

    console.log(`[palette-service] Prepared ${updatedItems.length} clock component(s) for palette "${palette.name}"`);
    return updatedItems;
  }

  /**
   * Apply a specific palette to scene clocks (convenience method)
   * @param palette - The palette to apply
   * @param players - Array of scene blocks/players
   * @param sceneId - The ID of the scene being updated
   * @returns Array of ISceneItemsUpdate objects for store dispatch
   */
  applyPaletteToSceneClocks(palette: ColorPalette, players: ISceneBlock[], sceneId: Id): ISceneItemsUpdate[] {
    // Set as current palette and save
    this.setCurrentPalette(palette);

    // Apply to clocks
    return this.applyToSceneClocks(players, sceneId);
  }

  /**
   * Universal handler: Apply palette colors to ALL components with fontColor property
   * Labels get primaryAccent, everything else gets componentColor
   * @param players - Array of scene blocks/players
   * @param sceneId - The ID of the scene being updated
   * @returns Array of ISceneItemsUpdate objects for store dispatch
   */
  applyToAllComponents(players: ISceneBlock[], sceneId: Id): ISceneItemsUpdate[] {
    const palette = this.currentPalette$.value;
    if (!palette || !players || players.length === 0) {
      return [];
    }

    const updatedItems: ISceneItemsUpdate[] = [];

    // Iterate through all players in the scene
    players.forEach((player) => {
      const playerId = player.$.player;
      const playerItemId = player.$.id;

      // Try to find and color any component with fontColor (non-intrusive, crash-safe)
      try {
        if (player?.Data?.[0]) {
          Object.keys(player.Data[0]).forEach(componentKey => {
            try {
              const component = player.Data[0][componentKey];
              let colorToUse;

              switch (componentKey) {
                // Labels get primaryAccent (stands out from other components)
                case 'Label':
                  if (component?.[0]?.Font?.[0]?.$?.fontColor !== undefined) {
                    colorToUse = this.hexToDecimal(palette.primaryAccent);
                    if (colorToUse) {
                      updatedItems.push({
                        sceneId: sceneId,
                        sceneItemId: playerItemId,
                        values: [{
                          key: 'fontColor',
                          value: colorToUse,
                          storePath: 'Data[0].Label[0].Font[0].$'
                        }]
                      });
                    }
                  }
                  break;

                case 'LabelQueue':
                  if (component?.[0]?.Font?.[0]?.$?.fontColor !== undefined) {
                    colorToUse = this.hexToDecimal(palette.primaryAccent);
                    if (colorToUse) {
                      updatedItems.push({
                        sceneId: sceneId,
                        sceneItemId: playerItemId,
                        values: [{
                          key: 'fontColor',
                          value: colorToUse,
                          storePath: 'Data[0].LabelQueue[0].Font[0].$'
                        }]
                      });
                    }
                  }
                  break;

                // Icon has color in $.color (not Font) - uses headingColor for variety
                case 'Icon':
                  if (component?.[0]?.$.color !== undefined) {
                    colorToUse = this.hexToDecimal(palette.headingColor);
                    if (colorToUse) {
                      updatedItems.push({
                        sceneId: sceneId,
                        sceneItemId: playerItemId,
                        values: [{
                          key: 'color',
                          value: colorToUse,
                          storePath: 'Data[0].Icon[0].$'
                        }]
                      });
                    }
                  }
                  break;

                // Rss has fonts nested in Title and Description - uses secondaryAccents[0]
                case 'Rss':
                  if (palette.secondaryAccents && palette.secondaryAccents[0]) {
                    colorToUse = this.hexToDecimal(palette.secondaryAccents[0]);
                    if (colorToUse) {
                      // Apply to Title font if exists
                      if (component?.[0]?.Title?.[0]?.Font?.[0]?.$?.fontColor !== undefined) {
                        updatedItems.push({
                          sceneId: sceneId,
                          sceneItemId: playerItemId,
                          values: [{
                            key: 'fontColor',
                            value: colorToUse,
                            storePath: 'Data[0].Rss[0].Title[0].Font[0].$'
                          }]
                        });
                      }
                      // Apply to Description font if exists
                      if (component?.[0]?.Description?.[0]?.Font?.[0]?.$?.fontColor !== undefined) {
                        updatedItems.push({
                          sceneId: sceneId,
                          sceneItemId: playerItemId,
                          values: [{
                            key: 'fontColor',
                            value: colorToUse,
                            storePath: 'Data[0].Rss[0].Description[0].Font[0].$'
                          }]
                        });
                      }
                    }
                  }
                  break;

                // Default: Standard components with Font (Clock, Button, Form, Countdown, RssItem, etc.)
                default:
                  if (component?.[0]?.Font?.[0]?.$?.fontColor !== undefined) {
                    colorToUse = this.hexToDecimal(palette.componentColor);
                    if (colorToUse) {
                      updatedItems.push({
                        sceneId: sceneId,
                        sceneItemId: playerItemId,
                        values: [{
                          key: 'fontColor',
                          value: colorToUse,
                          storePath: `Data[0].${componentKey}[0].Font[0].$`
                        }]
                      });
                    }
                  }
                  break;
              }
            } catch (error) {
              // Non-intrusive: silently skip
            }
          });
        }
      } catch (error) {
        // Non-intrusive: silently skip
      }
    });

    return updatedItems;
  }

  /**
   * Apply the current palette to labels, buttons, AND clocks in a scene
   * @param players - Array of scene blocks/players
   * @param sceneId - The ID of the scene being updated
   * @returns Array of ISceneItemsUpdate objects for store dispatch
   */
  applyToSceneComponents(players: ISceneBlock[], sceneId: Id): ISceneItemsUpdate[] {
    // Use the universal handler instead of individual methods
    return this.applyToAllComponents(players, sceneId);
  }

  /**
   * Apply a specific palette to labels, buttons, AND clocks in a scene (convenience method)
   * @param palette - The palette to apply
   * @param players - Array of scene blocks/players
   * @param sceneId - The ID of the scene being updated
   * @returns Array of ISceneItemsUpdate objects for store dispatch
   */
  applyPaletteToScene(palette: ColorPalette, players: ISceneBlock[], sceneId: Id): ISceneItemsUpdate[] {
    // Set as current palette and save
    this.setCurrentPalette(palette);

    // Apply to labels, buttons, and clocks
    return this.applyToSceneComponents(players, sceneId);
  }
}
